import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ShieldCheck, CheckCircle2, XCircle, AlertTriangle, CircleDashed,
  RefreshCw, Copy, Check, ExternalLink, Loader2, MousePointerClick,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pass: { Icon: CheckCircle2, color: "text-[#1e8e5a]", label: "PASS" },
  warn: { Icon: AlertTriangle, color: "text-[#b45309]", label: "WARN" },
  fail: { Icon: XCircle, color: "text-[#c81f16]", label: "FAIL" },
  pending: { Icon: CircleDashed, color: "text-[#b45309]", label: "WAITING" },
};

const OVERALL_META = {
  complete: {
    tint: "border-[#1e8e5a]/30 bg-[#1e8e5a]/[0.06] text-[#1e8e5a]",
    text: "End-to-end confirmed - the WMM tile handoff is fully operational.",
  },
  ready: {
    tint: "border-[#b45309]/30 bg-[#b45309]/[0.06] text-[#b45309]",
    text: "Builder side is ready. Waiting on the final step: a real tile click from WMM.",
  },
  issues: {
    tint: "border-[#c81f16]/30 bg-[#c81f16]/[0.06] text-[#c81f16]",
    text: "Something in the chain is broken - see the failing checks below.",
  },
};

/**
 * One-click SSO Handoff Smoke Test. Auto-runs on load, verifies every link of
 * the WMM -> Builder handshake we can reach from this side, and tracks whether
 * a REAL tile handoff has ever completed.
 */
export default function SsoCheckPage() {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(true);
  const [copied, setCopied] = useState(false);
  const ranRef = useRef(false);

  const builderUrl = window.location.origin;

  const run = useCallback(async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/sso/smoke-test`);
      setResult(data);
    } catch (e) {
      toast.error("Smoke test could not run", {
        description: e?.response?.data?.detail || "Backend unreachable - check the service and try again.",
      });
    } finally {
      setRunning(false);
    }
  }, []);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    run();
  }, [run]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(builderUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      toast.error("Copy failed - select and copy the URL manually.");
    }
  };

  const overall = result ? OVERALL_META[result.overall] : null;

  return (
    <div data-testid="sso-check-page" className="min-h-[calc(100vh-44px)] bg-[#f7f7f8] px-4 py-8">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-[#0050c0]" />
              <h1 className="font-nunito text-[20px] font-extrabold text-[#020202]">SSO Handoff Smoke Test</h1>
            </div>
            <p className="mt-1 font-poppins text-[13px] leading-relaxed text-[#6e6e6e]">
              Confirms every link of the Web Marketing Machine &rarr; Builder handshake. Re-run any time - especially
              right after Joe wires the Builder URL into WMM.
            </p>
          </div>
          <Button
            type="button"
            data-testid="sso-check-run-button"
            onClick={run}
            disabled={running}
            className="shrink-0 rounded-[10px] bg-[#0050c0] font-nunito text-[11px] font-extrabold uppercase tracking-[0.08em] text-white hover:bg-[#0343a0]"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={13} className="mr-1.5" />}
            {running ? "Running" : "Re-run"}
          </Button>
        </div>

        {/* Step 1: Builder URL to hand to WMM */}
        <div className="mt-6 rounded-[12px] border border-black/10 bg-white p-5 shadow-[0_4px_18px_rgba(2,2,2,0.05)]">
          <p className="font-nunito text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#9a9a9a]">
            Step 1 - Wire this into WMM
          </p>
          <p className="mt-1.5 font-poppins text-[13px] text-[#383838]">
            Set <code className="rounded bg-black/[0.05] px-1.5 py-0.5 font-mono text-[12px]">SSO_APP_BUILDER_URL</code> on
            the WMM host to this Builder URL:
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code
              data-testid="builder-url-value"
              className="flex-1 truncate rounded-[10px] border border-black/10 bg-[#f7f7f8] px-3 py-2.5 font-mono text-[12px] text-[#383838]"
            >
              {builderUrl}
            </code>
            <Button
              type="button"
              data-testid="builder-url-copy"
              onClick={copyUrl}
              variant="outline"
              className="h-10 rounded-[10px] border-black/15 px-3 font-nunito text-[11px] font-extrabold uppercase tracking-[0.06em] text-[#383838] hover:bg-black/[0.04]"
            >
              {copied ? <Check size={14} className="text-[#1e8e5a]" /> : <Copy size={14} />}
              <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
            </Button>
          </div>
        </div>

        {/* Step 2: automated checklist */}
        <div className="mt-4 rounded-[12px] border border-black/10 bg-white p-5 shadow-[0_4px_18px_rgba(2,2,2,0.05)]">
          <div className="flex items-center justify-between">
            <p className="font-nunito text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#9a9a9a]">
              Step 2 - Automated chain checks
            </p>
            {result && (
              <span className="font-poppins text-[11px] text-[#9a9a9a]">
                ran {new Date(result.ran_at).toLocaleTimeString()}
              </span>
            )}
          </div>

          {overall && (
            <div
              data-testid="sso-check-overall"
              className={`mt-3 rounded-[10px] border px-3.5 py-2.5 font-nunito text-[12px] font-extrabold ${overall.tint}`}
            >
              {overall.text}
            </div>
          )}

          <div className="mt-3 divide-y divide-black/[0.06]">
            {running && !result && (
              <div className="space-y-3 py-2" data-testid="sso-check-loading">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-4 w-4 animate-pulse rounded-full bg-black/[0.08]" />
                    <div className="h-3.5 flex-1 animate-pulse rounded bg-black/[0.06]" />
                  </div>
                ))}
              </div>
            )}
            {result?.checks?.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.pending;
              const { Icon } = meta;
              return (
                <div key={c.id} data-testid={`sso-check-item-${c.id}`} className="flex items-start gap-3 py-3">
                  <Icon size={17} className={`mt-0.5 shrink-0 ${meta.color}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-nunito text-[13px] font-extrabold text-[#383838]">{c.label}</p>
                      <span className={`shrink-0 font-nunito text-[9px] font-extrabold uppercase tracking-[0.14em] ${meta.color}`}>
                        {meta.label}
                      </span>
                    </div>
                    <p className="mt-0.5 break-words font-poppins text-[12px] leading-snug text-[#6e6e6e]">{c.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step 3: the one manual step */}
        <div className="mt-4 rounded-[12px] border border-black/10 bg-white p-5 shadow-[0_4px_18px_rgba(2,2,2,0.05)]">
          <p className="font-nunito text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#9a9a9a]">
            Step 3 - The real thing
          </p>
          <div className="mt-2 flex items-start gap-3">
            <MousePointerClick size={18} className="mt-0.5 shrink-0 text-[#f07000]" />
            <p className="font-poppins text-[13px] leading-relaxed text-[#383838]">
              Open the Web Marketing Machine, sign in, and click the <strong>Website Builder</strong> tile. You should
              land back here already signed in - then re-run this test and the last check turns green.
            </p>
          </div>
          {result?.wmm_base_url && (
            <Button
              type="button"
              data-testid="open-wmm-button"
              onClick={() => window.open(result.wmm_base_url, "_blank", "noopener")}
              variant="outline"
              className="mt-3 rounded-[10px] border-black/15 font-nunito text-[11px] font-extrabold uppercase tracking-[0.06em] text-[#383838] hover:bg-black/[0.04]"
            >
              Open WMM
              <ExternalLink size={13} className="ml-1.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
